/*  MUI  */
import { Box, ListSubheader, Typography } from '@mui/material';
import { Logo } from 'application/Shared/Components/App/Logo';

/*  Shared  */
import { Permission } from 'application/Shared/Components/Core';
import { FontSizes } from 'application/Shared/Constants';
import { SessionHelper } from 'application/Shared/Helpers';
import { Tenant } from 'application/Shared/types/tenant';

/* Hooks */
import { useSession } from 'hooks/redux/Selectors/session';

/*  React  */
import React from 'react';
import { Link, useParams } from 'react-router';

/*  Components  */
import { TenantSelector } from './TenantSelector';

/**
 * Component that renders the header section of the sidebar
 * @param {Object} props - Component props
 * @param {Object} [props.client] - Client object containing client information
 * @param {Function} props.handleReturnToPartnerHub - Callback function when returning to partner hub
 * @param {boolean} props.showClient - Flag to determine if client information should be displayed
 * @param {string} props.title - Title text to display in the header
 * @returns {JSX.Element} Rendered sidebar header component
 */
export const SidebarHeader = ({
    client,
    handleReturnToPartnerHub,
    showClient,
    title
}: {
    client?: Tenant;
    handleReturnToPartnerHub: () => void;
    showClient: boolean;
    title: string;
}): JSX.Element => {
    const clientName = client?.name || '';
    const { partnerId } = useParams();

    const session = useSession();
    const sessionHelper = new SessionHelper(session);
    const tenantIdForLink = partnerId || sessionHelper.getTenantId();

    return (
        <Box data-testid="sidebaRenderSubheader">
            <Permission required="partner">
                <Link
                    to={`/partner/${tenantIdForLink}/organizations`}
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        marginTop: '24px'
                    }}
                    data-trackid="sidebarHeader_partner_orgs_routeTo"
                >
                    <Box
                        onClick={() => {
                            handleReturnToPartnerHub();
                        }}
                        aria-hidden="true"
                        width="100%"
                        display="flex"
                        justifyContent="center"
                        style={{ cursor: 'pointer' }}
                    >
                        <Logo
                            key={tenantIdForLink}
                            lightColor
                            checkTenantSettings
                            containerStyle={{ marginBottom: 0 }}
                            style={{
                                margin: '8px 8px 24px 8px',
                                maxWidth: '150px'
                            }}
                        />
                    </Box>
                </Link>
            </Permission>
            <ListSubheader style={{ marginTop: '16px' }}>
                {showClient ? (
                    <Box data-testid="sidebarShowClientSubheader">
                        <Typography
                            variant="h4"
                            sx={{
                                color: 'sidebar.clientTitle',
                                fontWeight: 600,
                                marginBottom: '8px'
                            }}
                        >
                            {title}
                        </Typography>
                        <Box>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'sidebar.clientName',
                                    fontSize: FontSizes[0],
                                    fontWeight: 600,
                                    lineHeight: '14px',
                                    marginBottom: '8px',
                                    textTransform: 'capitalize'
                                }}
                            >
                                {clientName}
                            </Typography>
                        </Box>
                    </Box>
                ) : (
                    <Typography
                        variant="h4"
                        sx={{
                            color: 'sidebar.clientTitle',
                            fontWeight: 600,
                            marginBottom: '4px'
                        }}
                    >
                        {title}
                    </Typography>
                )}
            </ListSubheader>

            <Permission required={['partner']}>
                <Box marginX="8px" marginY="16px">
                    <TenantSelector />
                </Box>
            </Permission>
        </Box>
    );
};
